/*
 * Directive — the per-turn generative-UI dispatcher.
 *
 * The Intake Agent's `render` tool attaches a `directive_type` + payload
 * to `next_question`. ChatTurn mounts <Directive> below Orbit's text and
 * this component routes to the right primitive.
 *
 * Bubble-flavoured directive types (`bubble`, `bubble_chips`,
 * `bubble_text`) are NO-OPS here — the existing chat-turn chip row and
 * composer already handle them. Other primitives have dedicated cards.
 *
 *   confirm                → ConfirmCard
 *   offer_research         → OfferResearchCard
 *   bubble_scale           → ScalePicker
 *
 *   v0.5 adaptive flow ──
 *   marketplace_matches    → MarketplaceMatchesCard
 *   propose_prd_draft      → PrdDraftProposalCard
 *   offer_paid_human       → PaidHumanCard
 */

import React from 'react';

import type {
  DirectiveType, DirectivePayload, MarketplaceMatch,
} from '../../lib/dossier-types';

import { ConfirmCard } from './ConfirmCard';
import { OfferResearchCard } from './OfferResearchCard';
import { ScalePicker } from './ScalePicker';
import { PrdDraftProposalCard } from './PrdDraftProposalCard';
import { PaidHumanCard } from './PaidHumanCard';
import { MarketplaceMatchesCard } from './MarketplaceMatchesCard';
import { EnterL2Card } from './EnterL2Card';


export interface DirectiveProps {
  type: DirectiveType;
  payload: DirectivePayload | null | undefined;
  /** Confirm + scale + bubble_chips action callbacks all funnel
   *  through one handler so the chat parent has a single hook to wire
   *  up. The directive type tells the parent which path the value
   *  belongs to. */
  onAction?: (value: string, label: string) => void;
  onScan?: (url: string) => void;
  onDecline?: () => void;
  onApproachPick?: (approachId: string) => void;
  onScale?: (value: number) => void;
  /** Which approach is mid-write (RouteCard). */
  picking?: string | null;
  disabled?: boolean;
  /** Side-panel compact rendering (heading + buttons only on PRD/shelf cards).
   *  Full detail when false (intake / expanded full-screen). */
  compact?: boolean;

  // ── v0.5 directive handlers ──────────────────────────────────
  /** propose_prd_draft → user clicked "Draft the PRD". */
  onDraftPrd?: () => void;
  /** propose_prd_draft → user wants to edit something first. */
  onEditBeforePrd?: () => void;
  /** propose_prd_draft → user wants to add more context. */
  onAddMoreBeforePrd?: () => void;
  /** Drafting in progress (parent toggles while the draft_prd tool runs). */
  drafting?: boolean;
  /** offer_paid_human → user clicked "See more times" (cal.com fallback). */
  onOpenCalendar?: (url: string) => void;
  /** offer_paid_human → user picked a mock slot. Parent calls
   *  /api/intake/paid-human/book to persist + pushes a confirmation
   *  chat turn. Card waits for resolution before flipping. */
  onBookPaidHumanSlot?: (slot: {
    slotId: string;
    dateLabel: string;
    timeLabel: string;
    consultantName?: string;
    consultantRole?: string;
  }) => Promise<void> | void;
  /** offer_paid_human → user dismissed the card. */
  onDismissPaidHuman?: () => void;
  /** offer_paid_human → user wants to go back to the prior card (e.g. the
   *  deep-scope fork they came from). Parent removes the slot-picker turn. */
  onBackPaidHuman?: () => void;
  /** marketplace_matches → user picked a marketplace agent. */
  onPickMarketplace?: (slug: string, match: MarketplaceMatch) => void;
  /** marketplace_matches → user picked Custom Build. */
  onPickCustomBuild?: () => void;
  /** marketplace_matches → which CTA is mid-write. */
  marketplacePicking?: 'marketplace' | 'custom' | null;

  // ── Slice 5 — enter_l2_self callbacks ───────────────────────────
  /** Continue → drop into L2 Phase 1 inline. Parent fires a synthetic
   *  turn that lets the Inquirer start pulling L2 fields. */
  onEnterL2Continue?: () => void;
  /** Talk to Solution Partner → open PaidHumanCard via the same path
   *  the recommendation pill uses. */
  onEnterL2TalkToPartner?: (payload: DirectivePayload) => void;
}


export const Directive: React.FC<DirectiveProps> = ({
  type, payload,
  onAction, onScan, onDecline, onApproachPick, onScale,
  picking, disabled, compact,
  // v0.5
  onDraftPrd, onEditBeforePrd, onAddMoreBeforePrd, drafting,
  onOpenCalendar, onBookPaidHumanSlot, onDismissPaidHuman, onBackPaidHuman,
  onPickMarketplace, onPickCustomBuild, marketplacePicking,
  // Slice 5
  onEnterL2Continue, onEnterL2TalkToPartner,
}) => {
  const p = payload || {};

  switch (type) {
    case 'confirm':
      return (
        <ConfirmCard
          payload={p}
          onAction={onAction || (() => {})}
          disabled={disabled}
        />
      );
    case 'offer_research':
      return (
        <OfferResearchCard
          payload={p}
          onScan={onScan || (() => {})}
          onDecline={onDecline}
          disabled={disabled}
        />
      );
    case 'bubble_scale':
      return (
        <ScalePicker
          payload={p}
          onPick={onScale || (() => {})}
          disabled={disabled}
        />
      );

    // ── v0.5 adaptive directives ────────────────────────────────
    case 'marketplace_matches':
      return (
        <MarketplaceMatchesCard
          payload={p}
          onPickMarketplace={onPickMarketplace || (() => {})}
          onPickCustomBuild={onPickCustomBuild || (() => {})}
          picking={marketplacePicking ?? null}
        />
      );
    case 'propose_prd_draft':
      return (
        <PrdDraftProposalCard
          payload={p}
          onDraft={onDraftPrd || (() => {})}
          onEdit={onEditBeforePrd}
          onAddMore={onAddMoreBeforePrd}
          drafting={drafting}
          compact={compact}
        />
      );
    case 'offer_paid_human':
      return (
        <PaidHumanCard
          payload={p}
          onOpenCalendar={onOpenCalendar || (() => {})}
          onBookSlot={onBookPaidHumanSlot}
          onDismiss={onDismissPaidHuman}
          onBack={onBackPaidHuman}
        />
      );
    case 'enter_l2_self':
      return (
        <EnterL2Card
          payload={p}
          onContinue={onEnterL2Continue}
          onTalkToPartner={onEnterL2TalkToPartner}
          onCreatePrd={onDraftPrd}
          disabled={disabled}
        />
      );

    case 'bubble':
    case 'bubble_chips':
    case 'bubble_text':
    default:
      return null;
  }
};

export default Directive;
